import React from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useSupabaseAuth } from '@/hooks/auth';
import { useToast } from '@/hooks/use-toast';
import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Mail, 
  UserCheck,
  RefreshCw
} from 'lucide-react';

const AccountStatusMessage: React.FC = () => {
  const { user, profile, refreshUserData } = useSupabaseAuth();
  const { toast } = useToast();

  if (!user || !profile) return null;

  const handleRefresh = async () => {
    try {
      await refreshUserData();
      toast({
        title: "Account status refreshed",
        description: "Your account information has been updated"
      });
    } catch (error) {
      toast({
        title: "Failed to refresh",
        description: "Please try again or contact support",
        variant: "destructive"
      });
    }
  };

  // Check if account needs approval
  if (!profile.is_approved) {
    return (
      <Alert className="border-yellow-200 bg-yellow-50">
        <Clock className="h-4 w-4 text-yellow-600" />
        <AlertDescription className="text-yellow-800">
          <div className="space-y-3">
            <div>
              <h4 className="font-medium mb-1">Account Pending Approval</h4>
              <p className="text-sm">
                Your account has been created successfully, but it's currently pending approval from an administrator.
              </p>
            </div>
            
            <div className="bg-yellow-100 p-3 rounded-md">
              <h5 className="font-medium text-sm mb-2 flex items-center gap-1">
                <UserCheck className="h-4 w-4" />
                What happens next?
              </h5>
              <ul className="text-xs space-y-1">
                <li>• An administrator will review your account within 24 hours</li>
                <li>• You'll receive an email notification once approved</li>
                <li>• After approval, you'll have full access to all courses and features</li>
              </ul>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                className="text-yellow-700 border-yellow-300 hover:bg-yellow-100"
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                Refresh Status
              </Button>
            </div>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  // Check if email is verified
  if (!user.email_confirmed_at) {
    return (
      <Alert className="border-blue-200 bg-blue-50">
        <Mail className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          <div className="space-y-3">
            <div>
              <h4 className="font-medium mb-1">Email Verification Required</h4>
              <p className="text-sm">
                Please check your email and click the verification link to complete your account setup.
              </p>
            </div>
            
            <div className="bg-blue-100 p-3 rounded-md">
              <h5 className="font-medium text-sm mb-2">Didn't receive the email?</h5>
              <ul className="text-xs space-y-1">
                <li>• Check your spam/junk folder</li>
                <li>• Make sure you entered the correct email address</li>
                <li>• Contact support if you continue having issues</li>
              </ul>
            </div>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  // Account is approved and active
  if (profile.is_approved && user.email_confirmed_at) {
    return (
      <Alert className="border-green-200 bg-green-50">
        <CheckCircle className="h-4 w-4 text-green-600" />
        <AlertDescription className="text-green-800">
          <div className="space-y-2">
            <h4 className="font-medium">Account Active</h4>
            <p className="text-sm">
              Welcome to Neo Prism Academy! Your account is fully activated and you have access to all courses and features.
            </p>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  return null;
};

export default AccountStatusMessage;